const DB_NAME = "cloudrasoi_db";
// v2: adds "id_map" (offline id -> server id) and "failed_actions" (sync dead-letter)
const DB_VERSION = 2;

let dbPromise = null;

// Initialize the local IndexedDB database (connection is reused)
export const initDB = () => {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = (event) => {
      console.error("Database failed to open:", event.target.error);
      dbPromise = null;
      reject(event.target.error);
    };

    request.onsuccess = (event) => {
      const db = event.target.result;
      // Another tab is upgrading the schema — release our connection so it can proceed
      db.onversionchange = () => {
        db.close();
        dbPromise = null;
      };
      resolve(db);
    };

    request.onupgradeneeded = (event) => {
      const db = event.target.result;

      // Store general caches (layouts, products, categories)
      if (!db.objectStoreNames.contains("cache")) {
        db.createObjectStore("cache");
      }

      // Store un-synced offline orders and actions
      if (!db.objectStoreNames.contains("sync_queue")) {
        db.createObjectStore("sync_queue", { autoIncrement: true });
      }

      // Offline order id -> server order id, kept across sync runs and reloads
      if (!db.objectStoreNames.contains("id_map")) {
        db.createObjectStore("id_map");
      }

      // Actions the server permanently rejected, kept so nothing is silently lost
      if (!db.objectStoreNames.contains("failed_actions")) {
        db.createObjectStore("failed_actions", { autoIncrement: true });
      }
    };
  });

  return dbPromise;
};

const notifyQueueChanged = () => {
  window.dispatchEvent(new Event("sync-queue-changed"));
};

const runRequest = async (storeName, mode, makeRequest) => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, mode);
    const store = transaction.objectStore(storeName);
    const request = makeRequest(store);

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

const readAll = async (storeName) => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, "readonly");
    const store = transaction.objectStore(storeName);
    const request = store.openCursor();
    const rows = [];

    request.onsuccess = (event) => {
      const cursor = event.target.result;
      if (cursor) {
        rows.push({ id: cursor.key, ...cursor.value });
        cursor.continue();
      } else {
        resolve(rows);
      }
    };
    request.onerror = () => reject(request.error);
  });
};

// Cache API results locally (write)
export const cacheData = async (key, data) => {
  try {
    await runRequest("cache", "readwrite", (store) => store.put(data, key));
    return true;
  } catch (error) {
    console.error("Failed to cache data:", error);
  }
};

// Retrieve cached API results locally (read)
export const getCachedData = async (key) => {
  try {
    const result = await runRequest("cache", "readonly", (store) => store.get(key));
    return result ?? null;
  } catch (error) {
    console.error("Failed to get cached data:", error);
    return null;
  }
};

// Add an order operation mutation to the offline synchronization queue
export const queueSyncAction = async (action) => {
  try {
    await runRequest("sync_queue", "readwrite", (store) =>
      store.add({ ...action, queuedAt: action.queuedAt || new Date().toISOString() })
    );
    notifyQueueChanged();
    return true;
  } catch (error) {
    console.error("Failed to queue sync action:", error);
    throw error;
  }
};

// Retrieve all un-synced queue actions in chronological order
export const getQueuedActions = async () => {
  try {
    return await readAll("sync_queue");
  } catch (error) {
    console.error("Failed to get queued actions:", error);
    return [];
  }
};

// Remove a synchronised action from the offline queue
export const dequeueAction = async (id) => {
  try {
    await runRequest("sync_queue", "readwrite", (store) => store.delete(id));
    notifyQueueChanged();
    return true;
  } catch (error) {
    console.error("Failed to delete queued action:", error);
  }
};

// Remember which server order an offline order became
export const setIdMapping = async (localId, serverId) => {
  try {
    await runRequest("id_map", "readwrite", (store) => store.put(String(serverId), localId));
  } catch (error) {
    console.error("Failed to save id mapping:", error);
    throw error;
  }
};

export const getIdMapping = async (localId) => {
  try {
    return (await runRequest("id_map", "readonly", (store) => store.get(localId))) || null;
  } catch (error) {
    console.error("Failed to read id mapping:", error);
    return null;
  }
};

// Move a permanently rejected action out of the queue into the failed list
export const moveToFailed = async (action, reason) => {
  const { id, ...rest } = action;
  try {
    await runRequest("failed_actions", "readwrite", (store) =>
      store.add({ ...rest, error: reason, failedAt: new Date().toISOString() })
    );
    await runRequest("sync_queue", "readwrite", (store) => store.delete(id));
  } catch (error) {
    console.error("Failed to move action to failed list:", error);
  }
  notifyQueueChanged();
};

export const getFailedActions = async () => {
  try {
    return await readAll("failed_actions");
  } catch (error) {
    console.error("Failed to get failed actions:", error);
    return [];
  }
};

export const removeFailedAction = async (id) => {
  try {
    await runRequest("failed_actions", "readwrite", (store) => store.delete(id));
  } catch (error) {
    console.error("Failed to remove failed action:", error);
  }
  notifyQueueChanged();
};

// Put a failed action back at the end of the queue for another attempt
export const retryFailedAction = async (failed) => {
  const { id, error, failedAt, ...action } = failed;
  await queueSyncAction(action);
  await removeFailedAction(id);
};
